import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Popper,
  ClickAwayListener,
  List,
  ListItemButton,
  Grow,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import apiService from "../../services/api";
import DefaultImage from "../../assets/images/DefaultImage.png";
import { getBaseImageUrl, isPdfFile, resolvePreviewSrc as resolvePreviewSrcUtil } from "../../utils/mediaUtils";
import CreateBookingWizard from "./create-booking/CreateBookingWizard";
import {
  CREATE_BOOKING_SK,
  clearCreateBookingDraft,
  clearCreateBookingWizardSession,
} from "./createBookingSessionStorage";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { THEME_PRIMARY, THEME_PRIMARY_LIGHT, THEME_PRIMARY_TINT } from "./bookingTheme";

/** Design tokens — match Create Booking mock */
const CARD_BORDER = "#dee2e6";

const KYC_NOT_FOUND_DEFAULT_MESSAGE = "KYC not found for this user";
const SECTION_TITLE = "#1a335d";
const LABEL_MUTED = "#6c757d";
const TABLE_HEADER_BG = "#BDE3F9";
const ACTIVE_CHIP_BG = "rgba(212, 255, 199, 1)";
const ACTIVE_CHIP_TEXT = "rgba(31, 113, 13, 1)";
const PHONE_BLUE = "#164387";

/** Min lengths before calling FilterUser (name can be shorter for better typeahead UX). */
const MIN_FILTER_NAME_CHARS = 1;
const MIN_FILTER_PHONE_EMAIL_CHARS = 1;
/** Phone search: only call FilterUser with a number once 10 local digits are entered. */
const MIN_PHONE_DIGITS_FOR_FILTER = 10;
const FILTER_DEBOUNCE_MS = 400;

const RIDE_HISTORY_ROWS_PER_PAGE_OPTIONS = [5, 10, 25] as const;
const RIDE_HISTORY_DEFAULT_ROWS = 10;

const cardSx = {
  p: "20px",
  mb: 2,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: "8px",
  bgcolor: "#fff",
  boxShadow: "none",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box" as const,
};

const sectionTitleSx = {
  fontWeight: 700,
  color: SECTION_TITLE,
  fontSize: "1rem",
  letterSpacing: "0.01em",
  mb: 2.5,
  lineHeight: 1.3,
};

const fieldLabelSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: LABEL_MUTED,
  mb: 0.75,
  display: "block",
};

const readOnlyValueSx = {
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#212529",
  lineHeight: 1.35,
};

const SortHeader = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
    {children}
    <KeyboardArrowUpIcon sx={{ fontSize: 15, color: "#868e96", ml: 0.25 }} />
  </Box>
);

/** Row from GET /User/FilterUser */
export interface FilterUserBookingRow {
  userid: number;
  userName: string;
  joinedDate: string;
  phoneNumber: string;
  email: string;
  userStatus: number;
}

function normalizeFilterUserRow(row: Record<string, unknown>): FilterUserBookingRow {
  return {
    userid: Number(row.userid ?? row.userId ?? 0),
    userName: String(row.userName ?? row.username ?? row.UserName ?? ""),
    joinedDate: String(row.joinedDate ?? row.JoinedDate ?? ""),
    phoneNumber: String(row.phoneNumber ?? row.phonenumber ?? row.PhoneNumber ?? ""),
    email: String(row.email ?? row.Email ?? ""),
    userStatus: Number(row.userStatus ?? row.userstatus ?? row.UserStatus ?? 0),
  };
}

function extractFilterUserList(res: unknown): FilterUserBookingRow[] {
  const r = res as { data?: unknown };
  const raw = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? (r as unknown[]) : [];
  return raw
    .filter((x): x is Record<string, unknown> => x != null && typeof x === "object" && !Array.isArray(x))
    .map((x) => normalizeFilterUserRow(x));
}

function formatDisplayPhone(phone: string | null | undefined): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length >= 10) {
    const last10 = digits.slice(-10);
    return `+91 ${last10.slice(0, 5)} ${last10.slice(5)}`;
  }
  if (String(phone ?? "").trim()) return String(phone).trim();
  return "NA";
}

function formatJoinedDateDisplay(iso: string | null | undefined): string {
  const s = String(iso ?? "").trim();
  if (!s) return "NA";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

/** Row from GET /Trip/GetTripByUserId (booking + trip summary) */
interface RideHistoryTripRow {
  bookingmasterid: number;
  vehiclename: string;
  planstartdate: string;
  planenddate: string;
  bookingamount: number | null;
}

function extractTripsByUserIdList(res: unknown): Record<string, unknown>[] {
  const r = res as { data?: unknown };
  const raw = Array.isArray(r?.data) ? r.data : [];
  return raw.filter(
    (x): x is Record<string, unknown> => x != null && typeof x === "object" && !Array.isArray(x)
  );
}

function normalizeRideHistoryTripRow(row: Record<string, unknown>): RideHistoryTripRow {
  return {
    bookingmasterid: Number(row.bookingmasterid ?? row.bookingMasterId ?? 0),
    vehiclename: String(row.vehiclename ?? row.vehicleName ?? "").trim() || "—",
    planstartdate: String(row.planstartdate ?? row.planStartDate ?? ""),
    planenddate: String(row.planenddate ?? row.planEndDate ?? ""),
    bookingamount:
      row.bookingamount == null || row.bookingamount === ""
        ? null
        : Number(row.bookingamount),
  };
}

function formatRideHistoryAmount(amount: number | null): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function waLinkFromPhone(phone: string | null | undefined): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const n = digits.length >= 10 ? digits.slice(-10) : digits;
  return n ? `https://wa.me/91${n}` : "#";
}

function telLinkFromPhone(phone: string | null | undefined): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (!digits) return "#";
  return `tel:+${digits.startsWith("91") ? digits : `91${digits.slice(-10)}`}`;
}

function digitsOnly(s: string): string {
  return String(s).replace(/\D/g, "");
}

/** India mobile: store/compare as 91 + up to 10 digits. Leading 91 is optional in input. */
const IN_PHONE_MAX_DIGITS = 12;

/** Raw digits only → canonical 91XXXXXXXXXX (max 12 digits). */
function normalizeIndiaPhoneDigits(rawDigits: string): string {
  let d = digitsOnly(rawDigits);
  if (!d) return "";
  if (d.startsWith("91")) {
    return d.slice(0, IN_PHONE_MAX_DIGITS);
  }
  return (`91${d}`).slice(0, IN_PHONE_MAX_DIGITS);
}

/** Display value for the phone TextField (+91 …). */
function formatPhoneFieldValue(normalizedDigits: string): string {
  const national = normalizeIndiaPhoneDigits(normalizedDigits);
  if (!national) return "";
  const local = national.length > 2 ? national.slice(2) : "";
  if (!local) return "+91 ";
  if (local.length <= 5) return `+91 ${local}`;
  return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
}

/** Split API / stored phone into +91 + 10-digit local (Create Booking is India-only in UI). */
function phoneFieldValueToMobileParts(phoneNumber: string): { local: string } {
  const d = digitsOnly(String(phoneNumber ?? ""));
  if (!d) return { local: "" };
  const n = normalizeIndiaPhoneDigits(d);
  return { local: n.startsWith("91") ? n.slice(2) : "" };
}

function buildPhoneDisplayForMatch(phoneLocal: string): string {
  return formatPhoneFieldValue(normalizeIndiaPhoneDigits("91" + digitsOnly(phoneLocal)));
}

function buildPhoneDigitsForApi(phoneLocal: string): string {
  return normalizeIndiaPhoneDigits("91" + digitsOnly(phoneLocal));
}

/** Comparable 10-digit mobile for equality (handles 91 vs no 91). */
function comparableMobile10(digits: string): string {
  const n = normalizeIndiaPhoneDigits(digits);
  if (n.length >= IN_PHONE_MAX_DIGITS) return n.slice(-10);
  if (n.length > 2 && n.startsWith("91")) return n.slice(2).slice(-10);
  return n.slice(-10);
}

/** True while typed name / phone / email still match the customer picked from the list */
function inputsStillMatchPick(row: FilterUserBookingRow, customerNameVal: string, phoneVal: string, emailVal: string): boolean {
  if (customerNameVal.trim() !== row.userName.trim()) return false;
  if (emailVal.trim().toLowerCase() !== row.email.trim().toLowerCase()) return false;
  const a = comparableMobile10(phoneVal);
  const b = comparableMobile10(row.phoneNumber);
  if (a.length >= 10 && b.length >= 10) return a === b;
  return normalizeIndiaPhoneDigits(phoneVal) === normalizeIndiaPhoneDigits(row.phoneNumber);
}

type CustomerFilterField = "name" | "phone" | "email";


export type KycUiSnapshot = {
  kycid: number | null;
  kycstatus: number | null;
  addressLine: string;
  alternateMobile: string;
  maskedAadhaar: string;
  dlNumber: string;
  aadhaarName: string;
  /** Resolved preview URL for <img> (empty = hide) */
  selfiePreviewUrl: string;
  /** When true, show Aadhaar OCR front + back; when false, single `aadhaarfile` (PDF or image) if present */
  aadhaarUseOcrSides: boolean;
  aadhaarOcrFrontUrl: string;
  aadhaarOcrBackUrl: string;
  /** When both OCR sides empty and `aadhaarfile` is a PDF */
  aadhaarPdfHref: string;
  /** When both OCR sides empty and `aadhaarfile` is not a PDF */
  aadhaarSingleFilePreviewUrl: string;
  dlFrontUrl: string;
  dlBackUrl: string;
  addressDocuments: { label: string; imageUrl?: string; pdfHref?: string }[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function extractKycListFromResponse(res: unknown): Record<string, unknown>[] {
  const r = res as { data?: unknown; result?: unknown } | unknown[] | null | undefined;
  if (r == null) return [];
  if (Array.isArray(r)) return r.filter((x): x is Record<string, unknown> => asRecord(x) != null);
  const obj = r as { data?: unknown; result?: unknown };
  if (Array.isArray(obj.data)) return obj.data.filter((x): x is Record<string, unknown> => asRecord(x) != null);
  if (Array.isArray(obj.result)) return obj.result.filter((x): x is Record<string, unknown> => asRecord(x) != null);
  return [];
}

/** Same resolution as UserDetails document previews (`REACT_APP_BASE_IMAGE_URL`). */
function kycAbsoluteUrl(path: string): string {
  const p = path.trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const base = getBaseImageUrl();
  return `${base.replace(/\/?$/, "")}${p.startsWith("/") ? p : `/${p}`}`;
}

const resolveKycPreview = (value: string | null | undefined): string =>
  resolvePreviewSrcUtil(value, DefaultImage);

function kycPathWithoutQuery(path: string): string {
  const s = String(path).trim();
  if (!s) return "";
  return s.split("?")[0] ?? s;
}

function strField(x: unknown): string {
  return typeof x === "string" ? x.trim() : "";
}

function pickDetail(d: Record<string, unknown>, row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = strField(d[k] ?? row[k]);
    if (v) return v;
  }
  return "";
}

/** Map last KYC row from API into UI snapshot (same document rules as UserDetailsPage). */
function buildKycUiSnapshot(row: Record<string, unknown>): KycUiSnapshot {
  const d = asRecord(row.kycdetails ?? row.kycDetails) ?? {};
  const aFront = pickDetail(d, row, "aadhaarocr_frontimg", "aadhaarocr_frontImg", "aadhaarOcrFrontImg");
  const aBack = pickDetail(d, row, "aadhaarocr_backimg", "aadhaarocr_backImg", "aadhaarOcrBackImg");
  const aFile = pickDetail(d, row, "aadhaarfile", "aadhaarFile");
  const selfie = pickDetail(d, row, "selfie_image", "selfieImage");
  const dlF = pickDetail(d, row, "dl_ocr_frontimage", "dl_ocr_frontImage", "dlOcrFrontImage");
  const dlB = pickDetail(d, row, "dl_ocr_backimage", "dl_ocr_backImage", "dlOcrBackImage");

  const addrParts = [
    strField(row.house),
    strField(row.addressline1 ?? row.addressLine1),
    strField(row.addressline2 ?? row.addressLine2),
    strField(row.city),
    strField(row.state),
    strField(row.pincode),
    strField(row.country),
  ].filter(Boolean);

  const bothOcrEmpty = !aFront && !aBack;

  let aadhaarUseOcrSides = !bothOcrEmpty;
  let aadhaarOcrFrontUrl = "";
  let aadhaarOcrBackUrl = "";
  let aadhaarPdfHref = "";
  let aadhaarSingleFilePreviewUrl = "";

  if (bothOcrEmpty && aFile) {
    aadhaarUseOcrSides = false;
    if (isPdfFile(kycPathWithoutQuery(aFile))) {
      aadhaarPdfHref = kycAbsoluteUrl(aFile);
    } else {
      aadhaarSingleFilePreviewUrl = resolveKycPreview(aFile);
    }
  } else {
    if (aFront) aadhaarOcrFrontUrl = resolveKycPreview(aFront);
    if (aBack) aadhaarOcrBackUrl = resolveKycPreview(aBack);
  }

  const selfiePreviewUrl = selfie ? resolveKycPreview(selfie) : "";

  const addressDocRaw = strField(row.addressdocument ?? row.addressDocument);
  const addressDocuments: { label: string; imageUrl?: string; pdfHref?: string }[] = [];
  if (addressDocRaw) {
    const parts = addressDocRaw.split(",").map((p) => p.trim()).filter(Boolean);
    parts.forEach((path, idx) => {
      const label = parts.length > 1 ? `File ${idx + 1}` : "Proof";
      if (isPdfFile(kycPathWithoutQuery(path))) {
        addressDocuments.push({ label, pdfHref: kycAbsoluteUrl(path) });
      } else {
        addressDocuments.push({ label, imageUrl: resolveKycPreview(path) });
      }
    });
  }

  return {
    kycid: row.kycid as number | null,
    kycstatus: row.kycstatus as number | null,
    addressLine: addrParts.length ? addrParts.join(", ") : "—",
    alternateMobile: strField(row.alternatemobileno ?? row.alternateMobileNo) || "—",
    maskedAadhaar:
      pickDetail(d, row, "maskedaadhaarnumber", "maskedAadhaarNumber", "maskedAadhaarNo") || "—",
    dlNumber: pickDetail(d, row, "dl_ocr_idnumber", "dl_ocr_idNumber", "dlOcrIdNumber") || "—",
    aadhaarName: pickDetail(d, row, "aadhaarname", "aadhaarName") || "—",
    selfiePreviewUrl,
    aadhaarUseOcrSides,
    aadhaarOcrFrontUrl,
    aadhaarOcrBackUrl,
    aadhaarPdfHref,
    aadhaarSingleFilePreviewUrl,
    dlFrontUrl: dlF ? resolveKycPreview(dlF) : "",
    dlBackUrl: dlB ? resolveKycPreview(dlB) : "",
    addressDocuments,
  };
}

const DROPDOWN_LABEL_SX = {
  width: 72,
  flexShrink: 0,
  fontSize: "0.65rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: LABEL_MUTED,
  lineHeight: 1.2,
};

/** Shared row body for customer search (Autocomplete + legacy Popper list). */
function CustomerFilterOptionInner({
  row,
  emphasis,
}: {
  row: FilterUserBookingRow;
  emphasis: CustomerFilterField;
}) {
  const name = row.userName.trim() || "—";
  const phoneRaw = formatDisplayPhone(row.phoneNumber);
  const phoneDisplay = phoneRaw === "NA" ? "—" : phoneRaw;
  const emailDisplay = row.email.trim() || "—";

  const primary =
    emphasis === "phone" ? phoneDisplay : emphasis === "email" ? emailDisplay : name;
  const label = emphasis === "phone" ? "Phone" : emphasis === "email" ? "Email" : null;

  const showHint = emphasis === "phone" || emphasis === "email";

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: label ? 1.25 : 0 }}>
        {label ? (
          <Typography component="span" sx={DROPDOWN_LABEL_SX}>
            {label}
          </Typography>
        ) : null}
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: emphasis === "name" ? 500 : 700,
            fontSize: "0.875rem",
            lineHeight: 1.4,
            color: "text.primary",
            wordBreak: "break-word",
          }}
        >
          {primary}
        </Typography>
      </Box>
      {showHint ? (
        <Typography
          sx={{
            mt: 0.75,
            ml: "calc(72px + 10px)",
            fontSize: "0.75rem",
            color: LABEL_MUTED,
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {emphasis === "phone" && (
            <>
              <Box component="span" sx={{ fontWeight: 600, color: "#495057" }}>
                {name}
              </Box>
              {emailDisplay !== "—" ? ` · ${emailDisplay}` : ""}
            </>
          )}
          {emphasis === "email" && (
            <>
              <Box component="span" sx={{ fontWeight: 600, color: "#495057" }}>
                {name}
              </Box>
              {phoneDisplay !== "—" ? ` · ${phoneDisplay}` : ""}
            </>
          )}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Popper list row — pick user */
function CustomerFilterDropdownOption({
  row,
  emphasis,
  onPick,
}: {
  row: FilterUserBookingRow;
  emphasis: CustomerFilterField;
  onPick: (r: FilterUserBookingRow) => void;
}) {
  return (
    <ListItemButton
      dense
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onPick(row)}
      sx={{
        alignItems: "stretch",
        py: 0.75,
        px: 1.5,
        borderRadius: 0,
        "&:hover": { bgcolor: "action.hover" },
        "&.Mui-focusVisible": { bgcolor: "action.selected" },
      }}
    >
      <CustomerFilterOptionInner row={row} emphasis={emphasis} />
    </ListItemButton>
  );
}

const kycThumbBoxSx = {
  width: { xs: 140, sm: 168 },
  height: { xs: 92, sm: 108 },
  bgcolor: "#e9ecef",
  borderRadius: "8px",
  border: `1px solid ${CARD_BORDER}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.7rem",
  color: LABEL_MUTED,
  overflow: "hidden" as const,
};

const KycThumb = ({
  label,
  imageUrl,
  pdfHref,
  openInNewTab,
}: {
  label: string;
  imageUrl?: string;
  pdfHref?: string;
  /** Match UserDetails: open full image in new tab */
  openInNewTab?: boolean;
}) => {
  const showImg = Boolean(imageUrl);
  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ ...fieldLabelSx, mb: 0.5, fontSize: "0.75rem" }}>{label}</Typography>
      <Box
        sx={{
          ...kycThumbBoxSx,
          ...(openInNewTab && showImg
            ? {
                cursor: "pointer",
                "&:hover": { borderColor: "rgba(22, 67, 135, 0.45)" },
              }
            : {}),
        }}
      >
        {showImg ? (
          <Box
            component="img"
            src={imageUrl}
            alt={label}
            onClick={
              openInNewTab && imageUrl
                ? () => window.open(imageUrl, "_blank", "noopener,noreferrer")
                : undefined
            }
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : pdfHref ? (
          <Box
            component="a"
            href={pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              p: 1,
              textAlign: "center",
              color: PHONE_BLUE,
              fontWeight: 600,
              fontSize: "0.75rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
              width: "100%",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "1.75rem", lineHeight: 1 }} aria-hidden>
              📄
            </Typography>
            View PDF
          </Box>
        ) : (
          "Preview"
        )}
      </Box>
    </Box>
  );
};

/** Same shape as EditBookingPage when returning to the booking list with filters restored. */
type BookingListReturnState = {
  filters?: { filters?: Record<string, string>; dateRange?: (string | null)[] };
  page?: number;
};

const CreateBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customerName, setCustomerName] = useState(() => {
    try {
      return sessionStorage.getItem(CREATE_BOOKING_SK.customerName) ?? "";
    } catch {
      return "";
    }
  });
  const [phoneLocal, setPhoneLocal] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CREATE_BOOKING_SK.phone) ?? "";
      return raw ? phoneFieldValueToMobileParts(raw).local : "";
    } catch {
      return "";
    }
  });
  const [email, setEmail] = useState(() => {
    try {
      return sessionStorage.getItem(CREATE_BOOKING_SK.email) ?? "";
    } catch {
      return "";
    }
  });
  const [basicUser, setBasicUser] = useState<FilterUserBookingRow | null>(() => {
    try {
      const raw = sessionStorage.getItem(CREATE_BOOKING_SK.basicUser);
      if (raw != null && raw !== "") {
        return JSON.parse(raw) as FilterUserBookingRow;
      }
    } catch {
      // ignore
    }
    return null;
  });
  const [searchError, setSearchError] = useState<string | null>(null);
  const [kycUi, setKycUi] = useState<KycUiSnapshot | null>(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);
  const [rideHistoryTrips, setRideHistoryTrips] = useState<RideHistoryTripRow[]>([]);
  const [rideHistoryLoading, setRideHistoryLoading] = useState(false);
  const [rideHistoryError, setRideHistoryError] = useState<string | null>(null);
  const [rideHistoryPage, setRideHistoryPage] = useState(0);
  const [rideHistoryRowsPerPage, setRideHistoryRowsPerPage] = useState(RIDE_HISTORY_DEFAULT_ROWS);
  const [bookingFlowOpen, setBookingFlowOpen] = useState(() => {
    try {
      return sessionStorage.getItem(CREATE_BOOKING_SK.bookingFlowOpen) === "1";
    } catch {
      return false;
    }
  });

  const phoneFieldWrapRef = useRef<HTMLDivElement>(null);
  const emailFieldWrapRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterUserBookingRow[]>([]);
  /** True during debounce + in-flight FilterUser — show "Searching…" before any "No customers found". */
  const [filterAwaitingResult, setFilterAwaitingResult] = useState(false);
  /** Which input is focused — used to emphasize matching line in the dropdown */
  const [customerFilterField, setCustomerFilterField] = useState<CustomerFilterField>("name");
  /** After picking from dropdown, skip one debounced fetch so the list does not reopen immediately */
  const skipNextFilterEffectRef = useRef(false);
  /** Name list: only after user has typed (not on empty focus). */
  const [nameAutocompleteOpen, setNameAutocompleteOpen] = useState(false);
  /** After blur, show error if phone has 1–9 digits (must be exactly 10 to search). */
  const [phoneFieldBlurred, setPhoneFieldBlurred] = useState(false);

  const phoneDisplayForMatch = useMemo(() => buildPhoneDisplayForMatch(phoneLocal), [phoneLocal]);
  const phoneDigitsLen = digitsOnly(phoneLocal).length;
  const phoneIncomplete = phoneDigitsLen > 0 && phoneDigitsLen < MIN_PHONE_DIGITS_FOR_FILTER;
  const phoneValidationError = phoneFieldBlurred && phoneIncomplete;

  const rideHistoryPaginatedRows = useMemo(() => {
    const start = rideHistoryPage * rideHistoryRowsPerPage;
    return rideHistoryTrips.slice(start, start + rideHistoryRowsPerPage);
  }, [rideHistoryTrips, rideHistoryPage, rideHistoryRowsPerPage]);

  /** Any clear (X) on name / phone / email resets the whole customer search block. */
  const resetAllCustomerFilters = useCallback(() => {
    setCustomerName("");
    setPhoneLocal("");
    setEmail("");
    setPhoneFieldBlurred(false);
    setBasicUser(null);
    setFilterOptions([]);
    setDropdownOpen(false);
    setNameAutocompleteOpen(false);
    setSearchError(null);
    setFilterAwaitingResult(false);
    setCustomerFilterField("name");
    setAnchorEl(null);
    setRideHistoryPage(0);
  }, []);

  const nameAutocompleteValue = useMemo(() => {
    if (!basicUser) return null;
    return inputsStillMatchPick(basicUser, customerName, phoneDisplayForMatch, email) ? basicUser : null;
  }, [basicUser, customerName, phoneDisplayForMatch, email]);

  const navigateBackToBookingList = useCallback(() => {
    clearCreateBookingDraft();
    const st = (location.state || {}) as BookingListReturnState;
    if (st.filters) {
      navigate("/booking-management", {
        state: { filters: st.filters, page: st.page },
      });
    } else {
      try {
        localStorage.removeItem("bookingExportFilters");
      } catch {
        // ignore
      }
      navigate("/booking-management", { state: { shouldRefetch: true } });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CREATE_BOOKING_SK.customerName, customerName);
    } catch {
      // ignore
    }
  }, [customerName]);

  useEffect(() => {
    try {
      const stored = buildPhoneDisplayForMatch(phoneLocal);
      sessionStorage.setItem(CREATE_BOOKING_SK.phone, stored.trim() ? stored : "");
    } catch {
      // ignore
    }
  }, [phoneLocal]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CREATE_BOOKING_SK.email, email);
    } catch {
      // ignore
    }
  }, [email]);

  useEffect(() => {
    try {
      if (basicUser == null) sessionStorage.removeItem(CREATE_BOOKING_SK.basicUser);
      else sessionStorage.setItem(CREATE_BOOKING_SK.basicUser, JSON.stringify(basicUser));
    } catch {
      // ignore
    }
  }, [basicUser]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CREATE_BOOKING_SK.bookingFlowOpen, bookingFlowOpen ? "1" : "0");
    } catch {
      // ignore
    }
  }, [bookingFlowOpen]);

  const applyPickUser = useCallback((row: FilterUserBookingRow) => {
    skipNextFilterEffectRef.current = true;
    setCustomerName(row.userName.trim());
    const p = phoneFieldValueToMobileParts(row.phoneNumber);
    setPhoneLocal(p.local);
    setEmail(row.email.trim());
    setBasicUser(row);
    setFilterOptions([]);
    setDropdownOpen(false);
    setNameAutocompleteOpen(false);
    setSearchError(null);
  }, []);

  /** Customer name is primary: if user clears it, clear phone & email (picked values / typos). */
  useEffect(() => {
    if (skipNextFilterEffectRef.current) return;
    if (customerName.trim().length < MIN_FILTER_NAME_CHARS) {
      setPhoneLocal("");
      setEmail("");
      setNameAutocompleteOpen(false);
    }
  }, [customerName]);

  useEffect(() => {
    const uid = basicUser?.userid;
    if (uid == null || !Number.isFinite(Number(uid)) || Number(uid) <= 0) {
      setKycUi(null);
      setKycLoading(false);
      setKycError(null);
      return;
    }
    let cancelled = false;
    setKycLoading(true);
    setKycError(null);
    setKycUi(null);
    apiService
      .getKYCByUserIdForCreateBooking(Number(uid))
      .then((res: unknown) => {
        if (cancelled) return;
        const wrap = res as { isSuccess?: boolean; message?: string };
        if (wrap && typeof wrap === "object" && wrap.isSuccess === false) {
          setKycUi(null);
          const apiMsg = typeof wrap.message === "string" ? wrap.message.trim() : "";
          setKycError(apiMsg !== "" ? apiMsg : KYC_NOT_FOUND_DEFAULT_MESSAGE);
          return;
        }
        const list = extractKycListFromResponse(res);
        const primary = list.length ? (list.at(-1) ?? null) : null;
        if (!primary) {
          setKycUi(null);
          setKycError(KYC_NOT_FOUND_DEFAULT_MESSAGE);
          return;
        }
        setKycUi(buildKycUiSnapshot(primary));
        setKycError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setKycUi(null);
          setKycError(e instanceof Error && e.message.trim() !== "" ? e.message : "Failed to load KYC.");
        }
      })
      .finally(() => {
        if (!cancelled) setKycLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [basicUser?.userid]);

  useEffect(() => {
    const uid = basicUser?.userid;
    if (uid == null || !Number.isFinite(Number(uid)) || Number(uid) <= 0) {
      setRideHistoryTrips([]);
      setRideHistoryLoading(false);
      setRideHistoryError(null);
      return;
    }
    let cancelled = false;
    setRideHistoryLoading(true);
    setRideHistoryError(null);
    setRideHistoryTrips([]);
    apiService
      .getTripsByUserId(Number(uid))
      .then((res: unknown) => {
        if (cancelled) return;
        const wrap = res as { isSuccess?: boolean; message?: string };
        if (wrap && typeof wrap === "object" && wrap.isSuccess === false) {
          setRideHistoryTrips([]);
          setRideHistoryError(
            typeof wrap.message === "string" ? wrap.message : "Ride history could not be loaded."
          );
          return;
        }
        const list = extractTripsByUserIdList(res).map(normalizeRideHistoryTripRow);
        setRideHistoryTrips(list);
        setRideHistoryError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setRideHistoryTrips([]);
          setRideHistoryError(e instanceof Error ? e.message : "Failed to load ride history.");
        }
      })
      .finally(() => {
        if (!cancelled) setRideHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [basicUser?.userid]);

  useEffect(() => {
    setRideHistoryPage(0);
  }, [basicUser?.userid]);

  useEffect(() => {
    const n = rideHistoryTrips.length;
    const maxPage = Math.max(0, Math.ceil(n / rideHistoryRowsPerPage) - 1);
    setRideHistoryPage((p) => (p > maxPage ? maxPage : p));
  }, [rideHistoryTrips.length, rideHistoryRowsPerPage]);

  useEffect(() => {
    const cn = customerName.trim();
    const em = email.trim();
    const phoneLocalDigitsLen = digitsOnly(phoneLocal).length;
    const eligible =
      cn.length >= MIN_FILTER_NAME_CHARS ||
      phoneLocalDigitsLen >= MIN_PHONE_DIGITS_FOR_FILTER ||
      em.length >= MIN_FILTER_PHONE_EMAIL_CHARS;

    if (!eligible) {
      setBasicUser(null);
      setFilterOptions([]);
      setFilterAwaitingResult(false);
      setDropdownOpen(false);
      setSearchError(null);
      return;
    }

    setBasicUser((prev) => {
      if (!prev) return prev;
      return inputsStillMatchPick(prev, customerName, phoneDisplayForMatch, email) ? prev : null;
    });

    if (skipNextFilterEffectRef.current) {
      skipNextFilterEffectRef.current = false;
      setFilterAwaitingResult(false);
      return;
    }

    setFilterAwaitingResult(true);
    if (customerFilterField !== "name") {
      setDropdownOpen(true);
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchError(null);
      try {
        const res = await apiService.filterUserForBooking({
          customername: cn.length >= MIN_FILTER_NAME_CHARS ? cn : undefined,
          phonenumber:
            phoneLocalDigitsLen >= MIN_PHONE_DIGITS_FOR_FILTER
              ? buildPhoneDigitsForApi(phoneLocal)
              : undefined,
          email: em.length >= MIN_FILTER_PHONE_EMAIL_CHARS ? em : undefined,
        });
        if (cancelled) return;
        const list = extractFilterUserList(res);
        setFilterOptions(list);
        if (customerFilterField !== "name") {
          setDropdownOpen(true);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Customer search failed.";
          setSearchError(msg);
          setFilterOptions([]);
          setDropdownOpen(false);
        }
      } finally {
        if (!cancelled) setFilterAwaitingResult(false);
      }
    }, FILTER_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [customerName, phoneLocal, phoneDisplayForMatch, email, customerFilterField]);

  if (bookingFlowOpen && basicUser != null) {
    return (
      <CreateBookingWizard
        customerUserId={basicUser.userid}
        customerName={basicUser.userName}
        customerPhone={basicUser.phoneNumber}
        onBack={() => {
          clearCreateBookingWizardSession();
          setBookingFlowOpen(false);
        }}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", pb: 4, width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          mx: 0,
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.5,
          boxSizing: "border-box",
        }}
      >
        {/* Customer Details — name: MUI Autocomplete; phone/email: debounced FilterUser Popper */}
        <Paper elevation={0} sx={{ ...cardSx, position: "relative", overflow: "visible" }}>
          <Typography sx={sectionTitleSx}>Customer Details</Typography>
          <ClickAwayListener
            onClickAway={() => {
              setDropdownOpen(false);
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: { xs: 2.5, sm: 3 },
                  alignItems: "start",
                }}
              >
                <Box>
                  <Typography component="label" sx={fieldLabelSx} htmlFor="create-booking-customer-name">
                    Customer Name
                    <Box component="span" sx={{ color: "#dc3545", ml: 0.25 }}>
                      *
                    </Box>
                  </Typography>
                  <Autocomplete<FilterUserBookingRow, false, false, false>
                    id="create-booking-customer-name"
                    value={nameAutocompleteValue}
                    inputValue={customerName}
                    open={
                      nameAutocompleteOpen &&
                      customerName.trim().length >= MIN_FILTER_NAME_CHARS
                    }
                    onOpen={() => {
                      if (customerName.trim().length >= MIN_FILTER_NAME_CHARS) {
                        setNameAutocompleteOpen(true);
                      }
                    }}
                    onClose={() => setNameAutocompleteOpen(false)}
                    onInputChange={(_, newInput, reason) => {
                      setCustomerFilterField("name");
                      if (reason === "clear") {
                        resetAllCustomerFilters();
                        return;
                      }
                      if (reason === "reset") return;
                      setCustomerName(newInput);
                      if (newInput.trim().length >= MIN_FILTER_NAME_CHARS) {
                        setNameAutocompleteOpen(true);
                      } else {
                        setNameAutocompleteOpen(false);
                      }
                    }}
                    onChange={(_, newValue) => {
                      if (newValue) applyPickUser(newValue);
                      else {
                        resetAllCustomerFilters();
                      }
                    }}
                    options={filterOptions}
                    filterOptions={(opts) => opts}
                    getOptionLabel={(o) => o.userName.trim() || "Customer"}
                    isOptionEqualToValue={(a, b) => Number(a?.userid) === Number(b?.userid)}
                    autoHighlight
                    blurOnSelect
                    clearOnBlur={false}
                    noOptionsText={
                      filterAwaitingResult ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            py: 1.5,
                            px: 2,
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={22} thickness={4} sx={{ color: PHONE_BLUE }} />
                          <Typography sx={{ fontSize: "0.8125rem", color: LABEL_MUTED, fontWeight: 500 }}>
                            Searching customers…
                          </Typography>
                        </Box>
                      ) : customerName.trim().length >= MIN_FILTER_NAME_CHARS ? (
                        "No customers found"
                      ) : (
                        "Start typing a name"
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ py: 0.5, width: "100%" }}>
                          <CustomerFilterOptionInner row={option} emphasis="name" />
                        </Box>
                      </li>
                    )}
                    componentsProps={{
                      paper: {
                        elevation: 2,
                        sx: {
                          mt: 0.5,
                          borderRadius: "4px",
                          border: `1px solid ${CARD_BORDER}`,
                          maxHeight: 320,
                          bgcolor: "#fff",
                        },
                      },
                      popupIndicator: {
                        sx: { display: "none" },
                      },
                    }}
                    ListboxProps={{
                      sx: {
                        py: 0,
                        maxHeight: 300,
                        "& .MuiAutocomplete-option": {
                          minHeight: 40,
                          alignItems: "flex-start",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search customer name…"
                        variant="outlined"
                        hiddenLabel
                        autoComplete="off"
                        onFocus={(e) => {
                          params.inputProps?.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
                          if (customerName.trim().length >= MIN_FILTER_NAME_CHARS) {
                            setNameAutocompleteOpen(true);
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start" sx={{ ml: 0.25 }}>
                                <SearchRoundedIcon sx={{ fontSize: 20, color: LABEL_MUTED }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#fff",
                            borderRadius: "4px",
                            pl: 0.5,
                            "& fieldset": { borderColor: CARD_BORDER },
                          },
                        }}
                      />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Box>
                <Box ref={phoneFieldWrapRef} sx={{ width: "100%" }}>
                  <Typography sx={fieldLabelSx}>Phone Number</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      border: `1px solid ${phoneValidationError ? "#dc3545" : CARD_BORDER}`,
                      borderRadius: "4px",
                      overflow: "hidden",
                      bgcolor: "#fff",
                      "&:focus-within": {
                        borderColor: phoneValidationError ? "#dc3545" : "primary.main",
                        borderWidth: "2px",
                      },
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        flex: "0 0 30%",
                        minWidth: "30%",
                        bgcolor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        py: "6px",
                        borderRight: `1px solid ${CARD_BORDER}`,
                        userSelect: "none",
                      }}
                    >
                      <Typography sx={{ color: "#666", fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.5 }}>
                        +91
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="tel"
                      inputMode="numeric"
                      value={phoneLocal}
                      placeholder="10-digit mobile"
                      error={phoneValidationError}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, "").slice(0, MIN_PHONE_DIGITS_FOR_FILTER);
                        setPhoneLocal(next);
                        if (next.length === 0 || next.length === MIN_PHONE_DIGITS_FOR_FILTER) {
                          setPhoneFieldBlurred(false);
                        }
                        setCustomerFilterField("phone");
                        setAnchorEl(phoneFieldWrapRef.current);
                        if (next.length >= MIN_PHONE_DIGITS_FOR_FILTER) {
                          setDropdownOpen(true);
                        }
                      }}
                      onFocus={() => {
                        setCustomerFilterField("phone");
                        setAnchorEl(phoneFieldWrapRef.current);
                        if (
                          phoneDigitsLen >= MIN_PHONE_DIGITS_FOR_FILTER &&
                          (filterAwaitingResult || filterOptions.length > 0)
                        ) {
                          setDropdownOpen(true);
                        }
                      }}
                      onBlur={() => {
                        setPhoneFieldBlurred(true);
                      }}
                      variant="outlined"
                      hiddenLabel
                      autoComplete="off"
                      InputProps={{
                        endAdornment:
                          phoneDigitsLen > 0 ? (
                            <InputAdornment position="end" sx={{ mr: 0.25 }}>
                              <IconButton
                                type="button"
                                size="small"
                                aria-label="Reset all customer filters"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={resetAllCustomerFilters}
                                edge="end"
                                sx={{ p: 0.35 }}
                              >
                                <ClearRoundedIcon sx={{ fontSize: 20, color: LABEL_MUTED }} />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          border: "none",
                          borderRadius: 0,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </Box>
                  {(phoneValidationError || phoneIncomplete) && (
                    <FormHelperText error={phoneValidationError} sx={{ mx: 0, mt: 0.5 }}>
                      {phoneValidationError
                        ? "Enter a valid 10-digit mobile number."
                        : `${MIN_PHONE_DIGITS_FOR_FILTER - phoneDigitsLen} more digit${
                            MIN_PHONE_DIGITS_FOR_FILTER - phoneDigitsLen === 1 ? "" : "s"
                          } to search.`}
                    </FormHelperText>
                  )}
                </Box>
                <Box ref={emailFieldWrapRef}>
                  <Typography sx={fieldLabelSx}>Email</Typography>
                  <TextField
                    value={email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmail(v);
                      setCustomerFilterField("email");
                      setAnchorEl(emailFieldWrapRef.current);
                      if (v.trim().length >= MIN_FILTER_PHONE_EMAIL_CHARS) {
                        setDropdownOpen(true);
                      }
                    }}
                    onFocus={() => {
                      setCustomerFilterField("email");
                      setAnchorEl(emailFieldWrapRef.current);
                      if (
                        email.trim().length >= MIN_FILTER_PHONE_EMAIL_CHARS &&
                        (filterAwaitingResult || filterOptions.length > 0)
                      ) {
                        setDropdownOpen(true);
                      }
                    }}
                    size="small"
                    fullWidth
                    variant="outlined"
                    hiddenLabel
                    autoComplete="off"
                    placeholder="Search by email…"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 0.25 }}>
                          <SearchRoundedIcon sx={{ fontSize: 20, color: LABEL_MUTED }} />
                        </InputAdornment>
                      ),
                      endAdornment:
                        email.trim().length > 0 ? (
                          <InputAdornment position="end" sx={{ mr: 0.25 }}>
                            <IconButton
                              type="button"
                              size="small"
                              aria-label="Reset all customer filters"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={resetAllCustomerFilters}
                              edge="end"
                              sx={{ p: 0.35 }}
                            >
                              <ClearRoundedIcon sx={{ fontSize: 20, color: LABEL_MUTED }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#fff",
                        borderRadius: "4px",
                        pl: 0.5,
                        "& fieldset": { borderColor: CARD_BORDER },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Popper
                open={
                  dropdownOpen &&
                  !!anchorEl &&
                  (customerName.trim().length >= MIN_FILTER_NAME_CHARS ||
                    digitsOnly(phoneLocal).length >= MIN_PHONE_DIGITS_FOR_FILTER ||
                    email.trim().length >= MIN_FILTER_PHONE_EMAIL_CHARS)
                }
                anchorEl={anchorEl}
                placement="bottom-start"
                transition
                style={{ zIndex: 1300 }}
                modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
                    <Paper
                      elevation={2}
                      sx={{
                        mt: 0.5,
                        maxHeight: 320,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: "4px",
                        minWidth: anchorEl ? Math.max(anchorEl.offsetWidth, 300) : 300,
                        maxWidth: "min(100vw - 32px, 440px)",
                        bgcolor: "#fff",
                      }}
                    >
                      <Box sx={{ overflow: "auto", flex: 1, py: 0 }}>
                        {filterAwaitingResult ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1.5,
                              py: 3,
                              px: 2,
                            }}
                          >
                            <CircularProgress size={24} thickness={4} sx={{ color: PHONE_BLUE }} />
                            <Typography sx={{ fontSize: "0.8125rem", color: LABEL_MUTED, fontWeight: 500 }}>
                              Searching customers…
                            </Typography>
                          </Box>
                        ) : filterOptions.length === 0 ? (
                          <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
                            <Typography sx={{ fontWeight: 700, color: SECTION_TITLE, fontSize: "0.9rem", mb: 0.5 }}>
                              No customers found
                            </Typography>
                          </Box>
                        ) : (
                          <List dense disablePadding sx={{ py: 0 }}>
                            {filterOptions.map((row) => (
                              <CustomerFilterDropdownOption
                                key={String(row.userid)}
                                row={row}
                                emphasis={customerFilterField}
                                onPick={applyPickUser}
                              />
                            ))}
                          </List>
                        )}
                      </Box>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
          </ClickAwayListener>
        </Paper>

        {searchError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSearchError(null)}>
            {searchError}
          </Alert>
        )}

        {/* Basic Information — one row: 5 fields + circular action icons */}
        <Paper elevation={0} sx={{ ...cardSx, position: "relative", overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
              pr: { xs: 0, sm: 0 },
            }}
          >
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Basic Information</Typography>
            <Chip
              label={
                basicUser == null
                  ? "NA"
                  : basicUser.userStatus === 1
                    ? "Active"
                    : "Inactive"
              }
              size="small"
              sx={{
                bgcolor:
                  basicUser == null
                    ? "#e9ecef"
                    : basicUser.userStatus === 1
                      ? ACTIVE_CHIP_BG
                      : "#e9ecef",
                color:
                  basicUser == null
                    ? "#495057"
                    : basicUser.userStatus === 1
                      ? ACTIVE_CHIP_TEXT
                      : "#495057",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 26,
                borderRadius: "13px",
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: { xs: "stretch", lg: "flex-end" },
              gap: { xs: 2.5, lg: 2 },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "repeat(3, 1fr)",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
                gap: { xs: 2, sm: 2.5, lg: 2 },
                flex: 1,
                minWidth: 0,
                width: "100%",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>User Name</Typography>
                <Typography sx={readOnlyValueSx}>{basicUser?.userName?.trim() || "NA"}</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>User ID</Typography>
                <Typography sx={readOnlyValueSx}>
                  {basicUser?.userid != null ? String(basicUser.userid) : "NA"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>Phone Number</Typography>
                <Typography sx={readOnlyValueSx}>
                  {basicUser ? formatDisplayPhone(basicUser.phoneNumber) : "NA"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={fieldLabelSx}>Email</Typography>
                <Typography sx={{ ...readOnlyValueSx, wordBreak: "break-word" }}>
                  {basicUser?.email?.trim() ? basicUser.email.trim() : "NA"}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  gridColumn: { xs: "1 / -1", sm: "auto", lg: "auto" },
                }}
              >
                <Typography sx={fieldLabelSx}>Joined Date</Typography>
                <Typography sx={readOnlyValueSx}>
                  {basicUser ? formatJoinedDateDisplay(basicUser.joinedDate) : "NA"}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                flexShrink: 0,
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                pb: { lg: 0.25 },
              }}
            >
              <IconButton
                component="a"
                href={basicUser ? waLinkFromPhone(basicUser.phoneNumber) : "#"}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!basicUser || !String(basicUser.phoneNumber ?? "").replace(/\D/g, "")}
                sx={{
                  bgcolor: "#25D366",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  "&:hover": { bgcolor: "#20bd5a" },
                }}
                aria-label="WhatsApp"
              >
                <WhatsAppIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                component="a"
                href={basicUser ? telLinkFromPhone(basicUser.phoneNumber) : "#"}
                disabled={!basicUser || !String(basicUser.phoneNumber ?? "").replace(/\D/g, "")}
                sx={{
                  bgcolor: PHONE_BLUE,
                  color: "#fff",
                  width: 44,
                  height: 44,
                  "&:hover": { bgcolor: "#12356d" },
                }}
                aria-label="Phone"
              >
                <PhoneIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* KYC — loaded from GET /KYC/GetKYCByUserId when customer is selected */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>KYC &amp; Compliance</Typography>
            {basicUser != null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {kycLoading && <CircularProgress size={22} sx={{ color: PHONE_BLUE }} />}
                {kycUi?.kycstatus != null && (
                  <Chip
                    label={kycUi.kycstatus === 1 ? "Verified" : "Pending"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: kycUi.kycstatus === 1 ? ACTIVE_CHIP_BG : "#e9ecef",
                      color: kycUi.kycstatus === 1 ? ACTIVE_CHIP_TEXT : "#495057",
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          {kycError && basicUser != null && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setKycError(null)}>
              {kycError}
            </Alert>
          )}
          {!basicUser ? (
            <Typography sx={{ fontSize: "0.875rem", color: LABEL_MUTED }}>Select a customer to load KYC.</Typography>
          ) : kycLoading && !kycUi ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={24} sx={{ color: PHONE_BLUE }} />
              <Typography sx={{ color: LABEL_MUTED, fontSize: "0.875rem" }}>Loading KYC…</Typography>
            </Box>
          ) : !kycUi && !kycError ? (
            <Typography sx={{ fontSize: "0.875rem", color: LABEL_MUTED }}>{KYC_NOT_FOUND_DEFAULT_MESSAGE}</Typography>
          ) : kycUi ? (
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                  mb: 2.5,
                  pb: 2.5,
                  borderBottom: `1px solid ${CARD_BORDER}`,
                }}
              >
                <Box>
                  <Typography sx={fieldLabelSx}>Name (as per Aadhaar)</Typography>
                  <Typography sx={readOnlyValueSx}>{kycUi.aadhaarName}</Typography>
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>Alternate mobile</Typography>
                  <Typography sx={readOnlyValueSx}>{kycUi.alternateMobile}</Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1" } }}>
                  <Typography sx={fieldLabelSx}>Address</Typography>
                  <Typography sx={{ ...readOnlyValueSx, fontWeight: 600, fontSize: "0.875rem" }}>{kycUi.addressLine}</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { md: "stretch" },
                  minHeight: { md: 140 },
                }}
              >
                {/* Aadhaar */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    pr: { md: 3 },
                    pb: { xs: 3, md: 0 },
                    borderBottom: { xs: `1px solid ${CARD_BORDER}`, md: "none" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flexShrink: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: SECTION_TITLE, fontSize: "0.9375rem", mb: 0.5 }}>
                        Aadhaar Card
                      </Typography>
                      <Typography sx={{ fontSize: "0.875rem", color: LABEL_MUTED, fontFamily: "monospace" }}>
                        {kycUi.maskedAadhaar}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {kycUi.aadhaarUseOcrSides ? (
                        <>
                          <KycThumb label="Front Side" imageUrl={kycUi.aadhaarOcrFrontUrl || undefined} openInNewTab />
                          <KycThumb label="Back Side" imageUrl={kycUi.aadhaarOcrBackUrl || undefined} openInNewTab />
                        </>
                      ) : kycUi.aadhaarPdfHref ? (
                        <KycThumb label="Aadhaar" pdfHref={kycUi.aadhaarPdfHref} />
                      ) : kycUi.aadhaarSingleFilePreviewUrl ? (
                        <KycThumb label="Aadhaar" imageUrl={kycUi.aadhaarSingleFilePreviewUrl} openInNewTab />
                      ) : null}
                      {kycUi.selfiePreviewUrl ? (
                        <KycThumb label="Selfie" imageUrl={kycUi.selfiePreviewUrl} openInNewTab />
                      ) : null}
                    </Box>
                  </Box>
                  {kycUi.addressDocuments.length > 0 ? (
                    <Box sx={{ pt: 2, borderTop: `1px solid ${CARD_BORDER}` }}>
                      <Typography sx={{ fontWeight: 700, color: SECTION_TITLE, fontSize: "0.9375rem", mb: 1.5 }}>
                        Current address proof
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {kycUi.addressDocuments.map((doc, idx) => (
                          <KycThumb
                            key={`addr-doc-${idx}-${doc.pdfHref ?? doc.imageUrl ?? ""}`}
                            label={doc.label}
                            imageUrl={doc.imageUrl}
                            pdfHref={doc.pdfHref}
                            openInNewTab
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    width: "1px",
                    bgcolor: CARD_BORDER,
                    alignSelf: "stretch",
                    flexShrink: 0,
                    mx: 0,
                  }}
                />

                {/* Driving License */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    pl: { md: 3 },
                    pt: { xs: 3, md: 0 },
                  }}
                >
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: SECTION_TITLE, fontSize: "0.9375rem", mb: 0.5 }}>
                      Driving License
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", color: LABEL_MUTED, fontFamily: "monospace" }}>
                      {kycUi.dlNumber === "—" ? "—" : kycUi.dlNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, flexShrink: 0, flexWrap: "wrap" }}>
                    <KycThumb label="Front Side" imageUrl={kycUi.dlFrontUrl || undefined} openInNewTab />
                    <KycThumb label="Back Side" imageUrl={kycUi.dlBackUrl || undefined} openInNewTab />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Paper>

        {/* Ride History */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
          <Typography sx={sectionTitleSx}>Ride History</Typography>
          <TableContainer
            sx={{
              width: "100%",
              maxWidth: "100%",
              border: `1px solid ${CARD_BORDER}`,
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: TABLE_HEADER_BG, fontWeight: 700, color: SECTION_TITLE, py: 1.25, borderBottom: `1px solid ${CARD_BORDER}` }}>
                    <SortHeader>EV Model</SortHeader>
                  </TableCell>
                  <TableCell sx={{ bgcolor: TABLE_HEADER_BG, fontWeight: 700, color: SECTION_TITLE, py: 1.25, borderBottom: `1px solid ${CARD_BORDER}` }}>
                    <SortHeader>Trip Start Date</SortHeader>
                  </TableCell>
                  <TableCell sx={{ bgcolor: TABLE_HEADER_BG, fontWeight: 700, color: SECTION_TITLE, py: 1.25, borderBottom: `1px solid ${CARD_BORDER}` }}>
                    <SortHeader>Trip End Date</SortHeader>
                  </TableCell>
                  <TableCell sx={{ bgcolor: TABLE_HEADER_BG, fontWeight: 700, color: SECTION_TITLE, py: 1.25, borderBottom: `1px solid ${CARD_BORDER}` }}>
                    <SortHeader>Distance</SortHeader>
                  </TableCell>
                  <TableCell sx={{ bgcolor: TABLE_HEADER_BG, fontWeight: 700, color: SECTION_TITLE, py: 1.25, borderBottom: `1px solid ${CARD_BORDER}` }}>
                    <SortHeader>Amount Paid</SortHeader>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {basicUser == null ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ borderBottom: "none", py: 3, color: LABEL_MUTED, textAlign: "center" }}>
                      Select a customer from search to view ride history.
                    </TableCell>
                  </TableRow>
                ) : rideHistoryLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ borderBottom: "none", py: 4, textAlign: "center" }}>
                      <CircularProgress size={28} sx={{ color: PHONE_BLUE }} />
                    </TableCell>
                  </TableRow>
                ) : rideHistoryError ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ borderBottom: "none", py: 2 }}>
                      <Alert severity="error" sx={{ py: 0.5 }}>
                        {rideHistoryError}
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : rideHistoryTrips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ borderBottom: "none", py: 3, color: LABEL_MUTED, textAlign: "center" }}>
                      No ride history for this customer.
                    </TableCell>
                  </TableRow>
                ) : (
                  rideHistoryPaginatedRows.map((row) => (
                    <TableRow key={String(row.bookingmasterid)}>
                      <TableCell sx={{ borderBottom: `1px solid #e9ecef`, py: 1.5, color: "#212529" }}>{row.vehiclename}</TableCell>
                      <TableCell sx={{ borderBottom: `1px solid #e9ecef`, py: 1.5, color: "#212529" }}>
                        {formatJoinedDateDisplay(row.planstartdate)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid #e9ecef`, py: 1.5, color: "#212529" }}>
                        {formatJoinedDateDisplay(row.planenddate)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid #e9ecef`, py: 1.5, color: "#212529" }}>—</TableCell>
                      <TableCell sx={{ borderBottom: `1px solid #e9ecef`, py: 1.5, color: "#212529", fontWeight: 600 }}>
                        {formatRideHistoryAmount(row.bookingamount)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {basicUser != null &&
            !rideHistoryLoading &&
            !rideHistoryError &&
            rideHistoryTrips.length > 0 ? (
              <TablePagination
                component="div"
                count={rideHistoryTrips.length}
                page={rideHistoryPage}
                onPageChange={(_, nextPage) => setRideHistoryPage(nextPage)}
                rowsPerPage={rideHistoryRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRideHistoryRowsPerPage(Number.parseInt(e.target.value, 10));
                  setRideHistoryPage(0);
                }}
                rowsPerPageOptions={[...RIDE_HISTORY_ROWS_PER_PAGE_OPTIONS]}
                sx={{
                  borderTop: `1px solid ${CARD_BORDER}`,
                  width: "100%",
                  boxSizing: "border-box",
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 48,
                    px: { xs: 1.5, sm: 2 },
                    py: 0.75,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    flexWrap: "wrap",
                    gap: 0.5,
                  },
                  "& .MuiTablePagination-spacer": {
                    display: "none",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    mb: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    mb: 0,
                    marginLeft: "auto",
                  },
                  "& .MuiTablePagination-actions": {
                    flexShrink: 0,
                    marginLeft: 0.5,
                  },
                }}
              />
            ) : null}
          </TableContainer>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2.5,
            pb: 2,
            pr: 0.5,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Button
            variant="outlined"
            disableElevation
            onClick={navigateBackToBookingList}
            sx={{
              borderColor: THEME_PRIMARY,
              color: THEME_PRIMARY,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9375rem",
              px: 4,
              py: 1,
              borderRadius: "4px",
              minWidth: 100,
              "&:hover": {
                borderColor: THEME_PRIMARY_LIGHT,
                bgcolor: THEME_PRIMARY_TINT,
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={basicUser == null || kycLoading || kycUi == null}
            onClick={() => {
              if (basicUser == null) {
                setSearchError("Select a customer from search before continuing.");
                return;
              }
              if (kycLoading || kycUi == null) {
                setSearchError(kycError ?? KYC_NOT_FOUND_DEFAULT_MESSAGE);
                return;
              }
              setSearchError(null);
              setBookingFlowOpen(true);
            }}
            sx={{
              bgcolor: "#FFC107",
              color: "#1a1a1a",
              fontWeight: 600,
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: "4px",
              minWidth: 100,
              "&:hover": { bgcolor: "#FFB300" },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateBookingPage;
